/**
 * 担当案件テーブル（デザイン view）の「アンケート」列セル。
 *
 * テーブルが**子票ベース**（1 行 = デザイン / 情報設計 子チケット 1 件）なので、
 * 1 セルが担当するアンケートは**最大 1 通**。件数バッジも内訳 tooltip も要らず、
 * 状態がそのまま出る。母票ベースだった頃は 1 セルに複数件を畳んでいて、
 * 「回答する2件回答済」のように数字がどちらに掛かるか読めなくなっていた。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 担当案件テーブルのアンケート列。子票 1 件ぶんの回答状態とモーダル起動を担う（回答ボタンはデザイン view の桜色）
 * @graph-connects EvaluationSurveyModal [calls] 回答モーダル表示
 */
import { useState } from 'react';

import { Badge } from '@cortex/web-design-system/components/ui/Badge';

import { EvaluationSurveyModal } from './EvaluationSurveyModal.js';
import type { EvaluationTask } from '../../../types/idt-design-evaluation.js';

interface EvaluationSurveyCellProps {
  /** この子票のアンケート。評価対象外／未完了なら null */
  task: EvaluationTask | null;
}

/**
 * 回答済みの tooltip 文言（回答者と回答日）。
 * @graph-connects none
 */
function answeredTitle(task: EvaluationTask): string {
  const at = task.respondedAt ? task.respondedAt.slice(0, 10) : '-';
  return `${task.respondedBy ?? '-'} が ${at} に回答`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business アンケート列セル。未回答なら回答ボタン、回答済みならバッジ、対象外は理由付きの「—」
 * @graph-connects EvaluationSurveyModal [calls] 回答モーダル表示
 */
export function EvaluationSurveyCell({ task }: EvaluationSurveyCellProps) {
  const [open, setOpen] = useState(false);

  // 評価対象そのものが無い子票（未完了 / Pt 未入力 / デザインで 20pt 未満）。
  // 理由は API から個別には返さないため、ここでは「対象外」であることだけを示す。
  if (task === null) {
    return (
      <span
        className="text-[var(--color-text-muted)]"
        title="完了済みかつ該当Pt が確定したデザイン / 情報設計の子チケットが対象です（デザインは 20pt 以上）"
      >
        —
      </span>
    );
  }

  if (task.state === 'answered') {
    return (
      <Badge variant="default" size="sm" title={answeredTitle(task)}>
        回答済
      </Badge>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        /*
          デザイン view 専用の行なので、色も view の桜色に合わせる（DevDesignViewSwitch の
          アクティブ側と同じ pink-300 / pink-900）。アクセント青のままだと開発 view の色が
          デザインのテーブルに紛れ込む。
        */
        className="rounded-full bg-pink-300 px-3 py-0.5 text-xs font-medium whitespace-nowrap text-pink-900 transition-opacity hover:opacity-80"
      >
        回答する
      </button>
      <EvaluationSurveyModal
        tasks={[task]}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
